import React, { useEffect, useRef } from 'react';
import './Awards.css';

const Awards: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.hector-fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="awards-section hector-horizon-section" id="awards" ref={sectionRef}>
      {/* Floating Art Elements */}
      <div className="floating-brushstrokes">
        <div className="floating-brushstroke brushstroke-1"></div>
        <div className="floating-brushstroke brushstroke-2"></div>
        <div className="floating-brushstroke brushstroke-3"></div>
      </div>

      {/* Decorative Award Elements */}
      <div className="decorative-elements">
        <div className="award-laurel award-laurel-left">
          <svg width="80" height="120" viewBox="0 0 80 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 20C15 15 25 12 35 20C30 35 20 45 10 50C15 65 25 75 35 85C30 95 20 100 10 95C5 85 8 75 10 60C8 45 5 35 10 20Z" fill="rgba(255,215,0,0.1)" stroke="rgba(255,215,0,0.3)" strokeWidth="1"/>
            <path d="M15 25C18 22 25 20 30 25C28 35 22 42 15 45C18 55 25 62 30 70C28 78 22 82 15 80C12 75 14 68 15 58C14 48 12 38 15 25Z" fill="rgba(255,215,0,0.15)"/>
          </svg>
        </div>
        <div className="award-laurel award-laurel-right">
          <svg width="80" height="120" viewBox="0 0 80 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M70 20C65 15 55 12 45 20C50 35 60 45 70 50C65 65 55 75 45 85C50 95 60 100 70 95C75 85 72 75 70 60C72 45 75 35 70 20Z" fill="rgba(255,215,0,0.1)" stroke="rgba(255,215,0,0.3)" strokeWidth="1"/>
            <path d="M65 25C62 22 55 20 50 25C52 35 58 42 65 45C62 55 55 62 50 70C52 78 58 82 65 80C68 75 66 68 65 58C66 48 68 38 65 25Z" fill="rgba(255,215,0,0.15)"/>
          </svg>
        </div>
        <div className="award-ribbon award-ribbon-top">
          <svg width="200" height="40" viewBox="0 0 200 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 20L40 5H160L180 20L160 35H40L20 20Z" fill="rgba(255,215,0,0.2)" stroke="rgba(255,215,0,0.4)" strokeWidth="1"/>
            <path d="M50 20L60 15H140L150 20L140 25H60L50 20Z" fill="rgba(255,215,0,0.3)"/>
          </svg>
        </div>
      </div>

      <div className="hector-sky-zone">
        <div className="awards-container">
          <div className="awards-header hector-fade-in-up">
            <h2 className="hector-section-title">Awards & Recognition</h2>
            <div className="hector-horizon-line"></div>
            <p className="awards-intro">
              A lifetime of artistic excellence and community leadership recognized through prestigious awards and honors
            </p>
          </div>

          {/* Major Awards Section */}
          <div className="awards-category hector-fade-in-up">
            <h3 className="category-title">Major Awards & Honors</h3>
            <div className="awards-grid">
              <div className="hector-card award-card">
                <div className="award-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#FFD700"/>
                    <path d="M12 6.5L12.5 10.5L16 11L12.5 11.5L12 15.5L11.5 11.5L8 11L11.5 10.5L12 6.5Z" fill="#FFA500"/>
                    <circle cx="12" cy="18" r="3" fill="#FFD700"/>
                    <path d="M9 21H15V22H9V21Z" fill="#DAA520"/>
                  </svg>
                </div>
                <h4 className="hector-card-title">Purchase Award</h4>
                <p className="award-description">
                  Recipient in first annual Galleries Fleet competition. Artwork included in newly established
                  Galleries permanent collection - significant recognition for emerging artist.
                </p>
              </div>

              <div className="hector-card award-card">
                <div className="award-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.5 3L8.5 4H4C3.45 4 3 4.45 3 5S3.45 6 4 6H20C20.55 6 21 5.55 21 5S20.55 4 20 4H15.5L14.5 3H9.5Z" fill="#8B4513"/>
                    <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19Z" fill="#D2691E"/>
                    <circle cx="8" cy="2" r="1" fill="#FF6B6B"/>
                    <circle cx="12" cy="2" r="1" fill="#4ECDC4"/>
                    <circle cx="16" cy="2" r="1" fill="#45B7D1"/>
                    <path d="M8 9H10V17H8V9ZM12 9H14V17H12V9Z" fill="#A0522D"/>
                  </svg>
                </div>
                <h4 className="hector-card-title">Western Washington Fair</h4>
                <p className="award-description">
                  Fourth place (multiple years) and Honorable Mention. Consistent recognition in regional competition
                  at Puyallup Fair.
                </p>
              </div>

              <div className="hector-card award-card">
                <div className="award-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9L15.09 9.74L12 16L8.91 9.74L2 9L8.91 8.26L12 2Z" fill="#FFD700" stroke="#DAA520" strokeWidth="0.5"/>
                    <circle cx="12" cy="12" r="2" fill="#FFF" opacity="0.8"/>
                    <path d="M12 4L13.5 9L18 9.5L13.5 10L12 15L10.5 10L6 9.5L10.5 9L12 4Z" fill="#FFA500"/>
                    <path d="M9 18L12 20L15 18" stroke="#DAA520" strokeWidth="1.5" fill="none"/>
                  </svg>
                </div>
                <h4 className="hector-card-title">St Thomas More Juried Christian Art Show</h4>
                <p className="award-description">
                  2nd Place Award and Spangle Award recipient. Recognition in religious art category.
                </p>
              </div>
            </div>
          </div>

          {/* Community Leadership Section */}
          <div className="awards-category hector-fade-in-up">
            <h3 className="category-title">Community Leadership</h3>
            <div className="community-award-highlight">
              <div className="hector-card featured-award">
                <div className="award-badge">
                  <span className="award-date">March 19, 1999</span>
                </div>
                <h4 className="hector-card-title">Community Leader Award</h4>
                <div className="award-details">
                  <p><strong>Presented by:</strong> McIlvaigh Middle School</p>
                  <p><strong>Citation:</strong> "For his dedication toward building a better community"</p>
                  <p><strong>Signed by:</strong> David Chesnick and Mrs. Margaret Clark</p>
                </div>
                <p className="award-description">
                  Recognition of his impact on youth and community development through art programs.
                </p>
              </div>
            </div>
          </div>

          {/* Professional Recognition Section */}
          <div className="awards-category hector-fade-in-up">
            <h3 className="category-title">Professional Recognition</h3>
            <div className="awards-grid">
              <div className="hector-card award-card international">
                <div className="award-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#4A90E2" stroke="#2E5C8A" strokeWidth="1"/>
                    <path d="M2 12H22M12 2C14.5 2 16.5 6.5 16.5 12S14.5 22 12 22S7.5 17.5 7.5 12S9.5 2 12 2Z" stroke="#FFF" strokeWidth="1.5" fill="none"/>
                    <path d="M8 8L10 6L12 8L14 6L16 8" stroke="#FFF" strokeWidth="1" fill="none"/>
                    <path d="M8 16L10 18L12 16L14 18L16 16" stroke="#FFF" strokeWidth="1" fill="none"/>
                    <circle cx="12" cy="12" r="2" fill="#FFD700"/>
                    <path d="M6 10C8 9 10 10 12 9C14 10 16 9 18 10" stroke="#90EE90" strokeWidth="1.5" fill="none"/>
                    <path d="M6 14C8 15 10 14 12 15C14 14 16 15 18 14" stroke="#90EE90" strokeWidth="1.5" fill="none"/>
                  </svg>
                </div>
                <h4 className="hector-card-title">International Artist Selection</h4>
                <p className="award-description">
                  One of only ten U.S. artists selected to exhibit in the Soviet Union (1990).
                  Prestigious international cultural exchange program. Exhibited in Moscow and
                  Leningrad (now St. Petersburg).
                </p>
              </div>

              <div className="hector-card award-card">
                <div className="award-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4" y="4" width="16" height="12" rx="1" fill="#FFF" stroke="#333" strokeWidth="1"/>
                    <rect x="4" y="4" width="16" height="3" fill="#FF6B6B"/>
                    <path d="M6 9H18M6 11H18M6 13H15" stroke="#333" strokeWidth="0.8"/>
                    <circle cx="19" cy="6" r="3" fill="#FFD700" stroke="#DAA520" strokeWidth="1"/>
                    <path d="M19 4L19.5 5.5L21 6L19.5 6.5L19 8L18.5 6.5L17 6L18.5 5.5L19 4Z" fill="#FFF"/>
                    <rect x="2" y="17" width="20" height="3" rx="1" fill="#E8E8E8" stroke="#CCC" strokeWidth="0.5"/>
                    <path d="M5 18.5H19" stroke="#AAA" strokeWidth="0.5"/>
                  </svg>
                </div>
                <h4 className="hector-card-title">American Artist Magazine Selection</h4>
                <p className="award-description">
                  Featured in "Illustrated survey of leading contemporaries" for worldwide distribution
                  at museums and bookstores. Spotlighted as "up and coming" artist.
                </p>
              </div>
            </div>
          </div>

          {/* Educational Recognition Section */}
          <div className="awards-category educational-excellence-section hector-fade-in-up">
            <h3 className="category-title">Educational Excellence</h3>
            <div className="education-journey">
              <div className="journey-header">
                <div className="journey-badge">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3L13.5 7.5L18 9L13.5 10.5L12 15L10.5 10.5L6 9L10.5 7.5L12 3Z" fill="#FFD700"/>
                    <circle cx="12" cy="12" r="8" stroke="#DAA520" strokeWidth="1" fill="none"/>
                    <path d="M8 12L10 14L16 8" stroke="#FFD700" strokeWidth="1.5" fill="none"/>
                  </svg>
                </div>
                <h4 className="journey-title">Academic Achievement Timeline</h4>
                <p className="journey-subtitle">A foundation of excellence built through prestigious institutions</p>
              </div>

              <div className="education-steps">
                <div className="education-step" data-year="1960s">
                  <div className="step-connector"></div>
                  <div className="step-icon">
                    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="16" rx="2" fill="#4A90E2" stroke="#2E5C8A" strokeWidth="1"/>
                      <rect x="6" y="7" width="12" height="1" fill="#FFF"/>
                      <rect x="6" y="9" width="12" height="1" fill="#FFF"/>
                      <rect x="6" y="11" width="8" height="1" fill="#FFF"/>
                      <circle cx="18" cy="6" r="3" fill="#FFD700"/>
                      <path d="M18 4.5L18.5 5.5L19.5 6L18.5 6.5L18 7.5L17.5 6.5L16.5 6L17.5 5.5L18 4.5Z" fill="#FFF"/>
                      <rect x="10" y="13" width="4" height="2" rx="1" fill="#FF6B6B"/>
                    </svg>
                  </div>
                  <div className="step-content">
                    <div className="step-badge">Age 16</div>
                    <h5 className="step-title">School of American Arts, Chicago</h5>
                    <p className="step-description">Won contest at age 16. Earned art correspondence courses. Early recognition of artistic talent.</p>
                    <div className="step-achievement">Contest Winner</div>
                  </div>
                </div>

                <div className="education-step" data-year="1960s">
                  <div className="step-connector"></div>
                  <div className="step-icon">
                    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L15.09 8.26L22 9L15.09 9.74L12 16L8.91 9.74L2 9L8.91 8.26L12 2Z" fill="#FFD700"/>
                      <circle cx="12" cy="12" r="6" stroke="#DAA520" strokeWidth="1" fill="none" opacity="0.5"/>
                      <path d="M12 6L13 9L16 9.5L13 10L12 13L11 10L8 9.5L11 9L12 6Z" fill="#FFA500"/>
                      <rect x="8" y="18" width="8" height="3" rx="1" fill="#4A90E2"/>
                      <path d="M10 19H14" stroke="#FFF" strokeWidth="1"/>
                    </svg>
                  </div>
                  <div className="step-content">
                    <div className="step-badge">Merit-Based</div>
                    <h5 className="step-title">Art Center Scholarship</h5>
                    <p className="step-description">Full scholarship to School of Arts and Design (Art Center) in California. Merit-based recognition of artistic potential.</p>
                    <div className="step-achievement">Full Scholarship</div>
                  </div>
                </div>

                <div className="education-step" data-year="1965">
                  <div className="step-connector final"></div>
                  <div className="step-icon">
                    <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="4" y="8" width="16" height="12" rx="1" fill="#2E5C8A" stroke="#4A90E2" strokeWidth="1"/>
                      <path d="M4 10H20" stroke="#4A90E2" strokeWidth="1"/>
                      <circle cx="12" cy="14" r="2" fill="#FFD700"/>
                      <path d="M12 12L12.5 13.5L14 14L12.5 14.5L12 16L11.5 14.5L10 14L11.5 13.5L12 12Z" fill="#FFF"/>
                      <path d="M8 4L12 2L16 4V8H8V4Z" fill="#FF6B6B" stroke="#CC5555" strokeWidth="0.5"/>
                      <circle cx="12" cy="6" r="1" fill="#FFF"/>
                      <rect x="6" y="18" width="12" height="2" rx="1" fill="#DAA520"/>
                    </svg>
                  </div>
                  <div className="step-content">
                    <div className="step-badge">1965</div>
                    <h5 className="step-title">Burnley School of Professional Arts</h5>
                    <p className="step-description">Graduated with top honors. Academic excellence in professional art training that launched his career.</p>
                    <div className="step-achievement">Top Honors Graduate</div>
                  </div>
                </div>
              </div>

              <div className="journey-conclusion">
                <div className="conclusion-icon">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#4A90E2" stroke="#2E5C8A" strokeWidth="1"/>
                    <path d="M8 12L10 14L16 8" stroke="#FFF" strokeWidth="2" fill="none"/>
                    <circle cx="12" cy="12" r="6" stroke="#FFD700" strokeWidth="1" fill="none" opacity="0.7"/>
                  </svg>
                </div>
                <h5 className="conclusion-text">Foundation for Artistic Excellence</h5>
              </div>
            </div>
          </div>

          {/* Grants and Funding Section */}
          <div className="awards-category hector-fade-in-up">
            <h3 className="category-title">Grants & Funding Recognition</h3>
            <div className="hector-card funding-highlight">
              <h4 className="hector-card-title">Tacoma Arts Commission</h4>
              <p className="award-description">
                Received funding twice through competitive contract program. Recognition noted that
                "many deserving and creative applicants have been unsuccessful" yet Hector attracted
                "both the support and admiration of the Arts Commission" for his "tireless commitment
                to kids and his gifts as an artist."
              </p>
            </div>
          </div>

          {/* Media Recognition Section */}
          <div className="awards-category media-recognition-section hector-fade-in-up">
            <h3 className="category-title">Media Recognition</h3>
            <div className="media-recognition-highlight">
              <div className="media-hero-banner">
                <div className="media-badge-container">
                  <div className="media-badge">
                    <span className="media-badge-text">FEATURED ARTIST</span>
                  </div>
                </div>
                <h4 className="media-hero-title">National & International Media Coverage</h4>
                <p className="media-hero-subtitle">
                  Recognized by major publications and featured as a leading contemporary artist
                </p>
              </div>

              <div className="media-features-grid">
                <div className="hector-card media-feature-card spotlight">
                  <div className="media-icon">
                    <svg width="70" height="70" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="16" rx="2" fill="#FFF" stroke="#333" strokeWidth="1.2"/>
                      <rect x="3" y="4" width="18" height="4" fill="#FF6B6B"/>
                      <circle cx="6" cy="6" r="0.8" fill="#FFF"/>
                      <circle cx="18" cy="6" r="0.8" fill="#FFF"/>
                      <rect x="5" y="10" width="14" height="1" fill="#333"/>
                      <rect x="5" y="12" width="14" height="1" fill="#333"/>
                      <rect x="5" y="14" width="10" height="1" fill="#333"/>
                      <rect x="5" y="16" width="12" height="1" fill="#333"/>
                      <circle cx="19" cy="5" r="3" fill="#FFD700" stroke="#DAA520" strokeWidth="1"/>
                      <path d="M19 3L19.7 4.3L21 5L19.7 5.7L19 7L18.3 5.7L17 5L18.3 4.3L19 3Z" fill="#FFF"/>
                      <rect x="1" y="20" width="22" height="2" rx="1" fill="#E8E8E8"/>
                    </svg>
                  </div>
                  <h4 className="hector-card-title">Magazine Cover Stories</h4>
                  <div className="media-list-enhanced">
                    <div className="media-item-enhanced">
                      <span className="media-publication">SoundLife Magazine</span>
                      <span className="media-type">Multiple Cover Features</span>
                    </div>
                    <div className="media-item-enhanced">
                      <span className="media-publication">Tacoma City Paper</span>
                      <span className="media-type">Cover Story</span>
                    </div>
                  </div>
                </div>

                <div className="hector-card media-feature-card spotlight">
                  <div className="media-icon">
                    <svg width="70" height="70" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="8" fill="#4A90E2" stroke="#2E5C8A" strokeWidth="1"/>
                      <path d="M12 4L13.5 8L18 8.5L14.5 11.5L15.5 16L12 13.5L8.5 16L9.5 11.5L6 8.5L10.5 8L12 4Z" fill="#FFD700" stroke="#DAA520" strokeWidth="0.5"/>
                      <circle cx="12" cy="12" r="3" fill="#FFF" opacity="0.9"/>
                      <path d="M10 12L11 13L14 10" stroke="#4A90E2" strokeWidth="1.5" fill="none"/>
                      <circle cx="18" cy="6" r="2" fill="#FF6B6B"/>
                      <path d="M17.3 5.3L18.7 6.7M17.3 6.7L18.7 5.3" stroke="#FFF" strokeWidth="0.8"/>
                      <circle cx="6" cy="18" r="2" fill="#4ECDC4"/>
                      <path d="M5.5 18H6.5M6 17.5V18.5" stroke="#FFF" strokeWidth="0.8"/>
                    </svg>
                  </div>
                  <h4 className="hector-card-title">Feature Articles & Profiles</h4>
                  <div className="media-list-enhanced">
                    <div className="media-item-enhanced">
                      <span className="media-publication">"Art without boundaries"</span>
                      <span className="media-type">International Artist Profile</span>
                    </div>
                    <div className="media-item-enhanced">
                      <span className="media-publication">"Finding Light in the Darkness"</span>
                      <span className="media-type">Artistic Style Recognition</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Professional Endorsements Section */}
      <div className="professional-endorsements-modern">
        <div className="endorsements-container">
          <div className="endorsements-header hector-fade-in-up">
            <div className="header-badge">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7V9L21 9ZM3 9V7L9 7V9L3 9ZM15 11V13L21 13V11L15 11ZM3 11V13L9 13V11L3 11ZM15 15V17L21 17V15L15 15ZM3 15V17L9 17V15L3 15ZM15 19V21L21 21V19L15 19ZM3 19V21L9 21V19L3 19Z" fill="currentColor"/>
                <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1"/>
              </svg>
              <span>Professional Testimonials</span>
            </div>
            <h2 className="endorsements-title">Leadership Endorsements</h2>
            <p className="endorsements-subtitle">
              Recognition from distinguished leaders across education, government, and community organizations
            </p>
            <div className="title-decoration">
              <div className="decoration-line"></div>
              <div className="decoration-star">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="decoration-line"></div>
            </div>
          </div>

          <div className="testimonials-showcase hector-fade-in-up">
            <div className="testimonial-card featured-testimonial">
              <div className="card-background-effect"></div>
              <div className="testimonial-header">
                <div className="authority-badge city-gov">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3L2 12H5V20H9V14H15V20H19V12H22L12 3Z" fill="currentColor"/>
                  </svg>
                  <span>City Government</span>
                </div>
                <div className="quote-symbol">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <blockquote className="testimonial-text">
                "Innovative and rewarding program... a generation of young artists will remember
                where their interest and opportunities came from."
              </blockquote>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Michael S. Sullivan</div>
                  <div className="author-title">Cultural Resources Division Manager</div>
                  <div className="author-org">City of Tacoma</div>
                </div>
                <div className="verification-seal">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <div className="card-accent-line"></div>
            </div>

            <div className="testimonial-card education-testimonial">
              <div className="card-background-effect"></div>
              <div className="testimonial-header">
                <div className="authority-badge education">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3L1 9L12 15L21 10.09V17H23V9L12 3ZM5 13.18V17.18L12 21L19 17.18V13.18L12 17L5 13.18Z" fill="currentColor"/>
                  </svg>
                  <span>Education</span>
                </div>
                <div className="quote-symbol">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <blockquote className="testimonial-text">
                "Students became more positive and productive through Hector's comprehensive impact beyond art."
              </blockquote>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Cenobio Macias</div>
                  <div className="author-title">Superintendent</div>
                  <div className="author-org">Tacoma Public Schools</div>
                </div>
                <div className="verification-seal">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <div className="card-accent-line"></div>
            </div>

            <div className="testimonial-card community-testimonial">
              <div className="card-background-effect"></div>
              <div className="testimonial-header">
                <div className="authority-badge community">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 4C18.2 4 20 5.8 20 8C20 10.2 18.2 12 16 12C13.8 12 12 10.2 12 8C12 5.8 13.8 4 16 4ZM8 4C10.2 4 12 5.8 12 8C12 10.2 10.2 12 8 12C5.8 12 4 10.2 4 8C4 5.8 5.8 4 8 4ZM8 13C10.67 13 16 14.33 16 17V20H0V17C0 14.33 5.33 13 8 13ZM16 13C18.67 13 24 14.33 24 17V20H16V17C16 15.9 15.33 14.81 14.07 14.06C15.27 13.4 16.6 13 16 13Z" fill="currentColor"/>
                  </svg>
                  <span>Community</span>
                </div>
                <div className="quote-symbol">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <blockquote className="testimonial-text">
                "Program is beneficial to our community through bilingual teaching abilities and community impact."
              </blockquote>
              <div className="testimonial-author">
                <div className="author-info">
                  <div className="author-name">Natalya K. Paloma-Quintal</div>
                  <div className="author-title">Director</div>
                  <div className="author-org">Centro Latino</div>
                </div>
                <div className="verification-seal">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L13.5 7.5L19 9L13.5 10.5L12 15L10.5 10.5L5 9L10.5 7.5L12 2Z" fill="currentColor"/>
                  </svg>
                </div>
              </div>
              <div className="card-accent-line"></div>
            </div>
          </div>

          <div className="endorsement-summary hector-fade-in-up">
            <div className="summary-card">
              <div className="summary-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8 12L10 14L16 8" stroke="currentColor" strokeWidth="2" fill="none"/>
                  <circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.5"/>
                </svg>
              </div>
              <h4 className="summary-title">Cross-Sector Recognition</h4>
              <p className="summary-text">
                Endorsed by leaders across government, education, and community organizations for
                transformative impact on Tacoma's artistic and cultural development.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Legacy Recognition */}
      <div className="hector-earth-zone">
        <div className="legacy-section hector-fade-in-up">
          <div className="hector-quote-container">
            <blockquote className="hector-quote-text">
              "A lifetime dedicated to artistic excellence and community service,
              bridging cultural boundaries and inspiring generations."
            </blockquote>
            <cite className="hector-quote-attribution">— Legacy of Hector Díaz</cite>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Awards;